import * as React from 'react';
import { ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly visible: boolean;
	readonly onHide: () => void;
	readonly onAccept: () => void;
	readonly hasConfiguration: boolean;
	readonly passwordSignInUserCount: number;
};

/** Obtains an informed confirmation before closing the password sign-in path for the whole structure. */
const ExclusiveModeConfirmDialog: React.FC<Props> = ({
	visible,
	onHide,
	onAccept,
	hasConfiguration,
	passwordSignInUserCount,
}) => {
	// Three branches, not two — the wording differs on purpose: a brand-new configuration means every
	// user of the account is still on password sign-in today, so the count alone does not tell the whole
	// story the way it does once a configuration already exists.
	const message = React.useMemo(() => {
		const exclusionNotice =
			'Le mot de passe sera fermé pour toute la structure. En cas d’indisponibilité du fournisseur ' +
			'd’identité, plus personne ne pourra se connecter et le rétablissement passera par l’éditeur.';

		if (!hasConfiguration) {
			return (
				`${passwordSignInUserCount} personne(s) de cette structure sont configurées pour le mot ` +
				'de passe et n’ouvriront plus de session — c’est-à-dire, aujourd’hui, tout le monde. ' +
				'Commencez en coexistence, passez vos utilisateurs au fournisseur d’identité, puis basculez.'
			);
		}

		if (passwordSignInUserCount > 0) {
			return (
				`${passwordSignInUserCount} personne(s) de cette structure sont configurées pour le mot ` +
				'de passe et n’ouvriront plus de session. Passez-les au fournisseur d’identité avant de ' +
				`continuer, ou restez en coexistence. ${exclusionNotice}`
			);
		}

		return `${exclusionNotice} Confirmer ?`;
	}, [hasConfiguration, passwordSignInUserCount]);

	return (
		<ConfirmDialog
			blockScroll
			closable
			draggable={false}
			visible={visible}
			onHide={onHide}
			header="Basculer en mode exclusif"
			message={message}
			acceptLabel="Confirmer"
			acceptSeverity="danger"
			accept={onAccept}
		/>
	);
};

export default ExclusiveModeConfirmDialog;
